/**
 * The option lists behind the page's four controls, defined once.
 *
 * The evaluation method and correction arrays were copied into both the card and the
 * sticky bar, and the "nill means all currencies" rule was known independently by the
 * results header, the statistics table and the sticky bar. Adding a correction method or
 * changing that sentinel meant finding every copy: the same drift that let the
 * dashboard hold a stale PowerAnalysisResult and a broken erf.
 */
import { ALL_CURRENCIES } from 'helpers/currency';

export type SelectOption = { value: string; label: string };

// The sentinel and its two rules now live with the other cross-surface currency logic,
// so the crons, the statistics API and the attention queue read the same definition
// this screen does. Re-exported rather than moved outright: these names are the
// vocabulary of this screen and its call sites should not have to know where they come
// from.
export { ALL_CURRENCIES, isSingleCurrencySite, revenueNeedsCurrency } from 'helpers/currency';

export const EVALUATION_METHOD_OPTIONS: SelectOption[] = [
  { value: 'frequentist', label: 'Frequentist' },
  { value: 'bayesian', label: 'Bayesian' },
];

export const CORRECTION_METHOD_OPTIONS: SelectOption[] = [
  { value: 'holm-bonferroni', label: 'Holm-Bonferroni' },
  { value: 'benjamini-hochberg', label: 'Benjamini-Hochberg' },
  { value: 'sidak', label: 'Šidák' },
  { value: 'none', label: 'No correction' },
];

/** Broadest view first, then alphabetical. */
export const buildCurrencyOptions = (currencies: readonly string[]): SelectOption[] =>
  [...currencies]
    .sort((a, b) => (a === ALL_CURRENCIES ? -1 : b === ALL_CURRENCIES ? 1 : a.localeCompare(b)))
    .map((c) => ({ value: c, label: c === ALL_CURRENCIES ? 'All currencies' : c.toUpperCase() }));

export const buildGoalOptions = (goals: readonly string[], labels?: Record<string, string>): SelectOption[] =>
  goals.map((goal) => ({ value: goal, label: labels?.[goal] ?? goal }));

/**
 * Series colours for every chart on the experiment pages.
 *
 * Four separate copies had drifted: three listed five colours and one listed nine, so a
 * sixth variant was a different colour in the combination chart than anywhere else.
 * Assigned by index and never cycled: beyond this list, a chart should group the tail
 * rather than reuse a hue and imply two series are the same.
 *
 * These are tokens, not hexes, so the series step for the surface they sit on - the dark
 * steps are chosen for the dark panel rather than being the light ones flipped.
 *
 * The order is validated, not chosen by eye: each adjacent pair clears the colour-blind
 * separation floor in all three CVD simulations as well as for normal vision. That is why
 * the brand's own blue and violet are not neighbours here - they sit only 10.4 apart for
 * normal vision, against a floor of 15, so a reader could not tell two adjacent variants
 * apart. Do not reorder this list.
 *
 * The list was cut to six at one point, which meant variants 7, 8 and 9 all fell through to
 * the neutral tail and drew as the same grey line - three series a legend could not tell
 * apart, presented as if they were one. Nine is the validated length; see the --chart-*
 * block in globals.css for why those three hues and that order.
 */
export const SERIES_COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(var(--chart-6))',
  'hsl(var(--chart-7))',
  'hsl(var(--chart-8))',
  'hsl(var(--chart-9))',
] as const;

/** The tail colour, for any series past the validated set. Neutral on purpose. */
export const SERIES_TAIL_COLOR = 'hsl(var(--barMuted))';

/**
 * Past the end of the list the tail is neutral rather than a repeat. Cycling would paint
 * the seventh variant the same as the first and imply they are one series - the comment
 * above always said "never cycled", but the modulo did exactly that.
 */
export const seriesColor = (index: number): string => SERIES_COLORS[index] ?? SERIES_TAIL_COLOR;

/**
 * The colour slots, assigned once per experiment and shared by every chart on the screen.
 *
 * `seriesColor` takes a position, so a variant keeps one colour only while every chart
 * agrees where it sits. They did not, and the cause was a different *set* rather than a
 * different order: the timeseries endpoint behind RateOverTime drops deployed variants
 * (`.filter(v => !v.deployed)`), while the stored statistics payload behind
 * StatisticsChart and CumulativeChart keeps them. Drop one variant from the front of one
 * list and every variant after it moves up a slot, so the same variant drew in one colour
 * on one chart and another colour on the chart beside it.
 *
 * Survivable while the palette ran blue, green, red — the first two slots were both cool,
 * and a swap read as noise. The token palette puts green and red in those slots, so a
 * shifted variant now reads as winning on one chart and losing on the next: a verdict,
 * produced by an accident of which rows a query happened to exclude.
 *
 * Ranking against the experiment's full variant list fixes it at the source. A chart that
 * plots a subset still asks for the same slot, because the slot is a property of the
 * variant rather than of the array it happens to appear in.
 *
 * Ascending id, not control-first: ids are stable and every caller already has them,
 * whereas "which one is the control" is not on every payload that needs a colour.
 */
export const variantColorOrder = (variants: readonly { id: number }[]): number[] =>
  variants.map((variant) => variant.id).sort((a, b) => a - b);

/** Tail colour for a variant missing from the order — it is not in this experiment. */
export const seriesColorForVariant = (variantId: string | number, order: readonly number[]): string =>
  seriesColor(order.indexOf(Number(variantId)));
